import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import '../styles/Scenes.css';
import { useTimeContext } from '../contexts/TimeContext';

// Define tower types for the map
interface MapTower {
  id: string;
  x: number;
  y: number;
  team: 'radiant' | 'dire' | 'neutral';
  type: 'tower' | 'barracks' | 'ancient' | 'shrine';
  tier?: number; // For towers
  lane?: 'top' | 'mid' | 'bot' | 'secret' | 'outpost' | 'base' | 'jungle';
  isDestroyed?: boolean;
  destroyedAt?: number; // Game time in seconds
}

// Add new interface for hero position data
interface HeroPosition {
  id: number;
  heroId: number;
  name: string;
  team: 'radiant' | 'dire';
  color: string;
  imageUrl: string;
  positions: { time: number; x: number; y: number }[];
}

const Scenes: React.FC = () => {
  const [selectedTeam, setSelectedTeam] = useState<'radiant' | 'dire'>('radiant');
  const [showGrid, setShowGrid] = useState(false);
  const [coordinates, setCoordinates] = useState<{ x: number; y: number } | null>(null);
  const [towers, setTowers] = useState<MapTower[]>([]);
  const [heroPositions, setHeroPositions] = useState<HeroPosition[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [matchId, setMatchId] = useState<string | null>("7581214139"); // Example match ID, can be set from props
  const mapRef = useRef<HTMLDivElement>(null);
  
  // Use the shared time context
  const {
    currentTimePoint,
    setCurrentTimePoint,
    gameDuration,
    setGameDuration,
    isPlaying,
    setIsPlaying,
    gameObjectives,
    setGameObjectives,
    radiantHeroes,
    direHeroes
  } = useTimeContext();
  
  // Convert time point to an index for networth data (0-10 range)
  const timeIndex = useMemo(() => {
    // Calculate time index based on how far we are in the game
    const minuteValue = Math.floor(currentTimePoint / 60);
    
    // Map minute values to index positions in our data array (0-10)
    // This is based on the game duration and divides it into 11 points
    const gameMinutes = Math.floor(gameDuration / 60);
    
    // Calculate what percentage of game time has elapsed
    const percentComplete = minuteValue / gameMinutes;
    
    // Map to our 11 points (0-10)
    // Use Math.min to ensure we don't exceed the array bounds
    const index = Math.min(10, Math.round(percentComplete * 10));
    
    console.log(`Time: ${minuteValue}m, Index: ${index}, Game Minutes: ${gameMinutes}`);
    return index;
  }, [currentTimePoint, gameDuration]);
  
  // Calculate total networth for both teams at current time
  const radiantTotalNetWorth = useMemo(() => {
    return radiantHeroes.reduce((sum, hero) => sum + hero.networth[timeIndex], 0);
  }, [radiantHeroes, timeIndex]);
  
  const direTotalNetWorth = useMemo(() => {
    return direHeroes.reduce((sum, hero) => sum + hero.networth[timeIndex], 0);
  }, [direHeroes, timeIndex]);
  
  // Create hero data with current networth values
  const radiantData = useMemo(() => {
    return radiantHeroes.map(hero => ({
      hero: {
        id: hero.id,
        name: hero.name,
        imageUrl: hero.imageUrl
      },
      netWorth: hero.networth[timeIndex],
      kda: hero.kda
    }));
  }, [radiantHeroes, timeIndex]);
  
  const direData = useMemo(() => {
    return direHeroes.map(hero => ({
      hero: {
        id: hero.id,
        name: hero.name,
        imageUrl: hero.imageUrl
      },
      netWorth: hero.networth[timeIndex],
      kda: hero.kda
    }));
  }, [direHeroes, timeIndex]);
  
  // Calculate advantage
  const advantage = useMemo(() => {
    const diff = direTotalNetWorth - radiantTotalNetWorth;
    const team = diff > 0 ? 'Dire' : 'Radiant';
    const value = Math.abs(diff);
    
    let displayText = 'Even';
    let colorClass = '#ffffff';
    
    if (Math.abs(diff) > 1000) {
      displayText = `${team} ${Math.round(value / 1000)}k+`;
      colorClass = team === 'Dire' ? '#ef5350' : '#66bb6a';
    }
    
    return { displayText, colorClass, value, team };
  }, [radiantTotalNetWorth, direTotalNetWorth]);

  // Handle map click to get coordinates
  const handleMapClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!mapRef.current) return;
    
    const rect = mapRef.current.getBoundingClientRect();
    const x = Math.floor((e.clientX - rect.left) / 30); // 30px per grid cell
    const y = Math.floor((e.clientY - rect.top) / 30);  // 30px per grid cell
    
    setCoordinates({ x, y });
    
    // Log the pixel coordinates for precise placement
    const pixelX = Math.round(e.clientX - rect.left);
    const pixelY = Math.round(e.clientY - rect.top);
    console.log(`Clicked at pixel coordinates: x=${pixelX}, y=${pixelY}`);
  }, []);
  
  // Fetch tower data from OpenDota API
  const fetchTowerData = useCallback(async () => {
    try {
      setIsLoading(true);
      
      // Updated tower coordinates based on the minimap image with the numbered grid
      const towerData: MapTower[] = [
        // Radiant Structures (Green)
        // Bottom lane
        { id: 'r_t1_bot', x: 450, y: 390, team: 'radiant', type: 'tower', tier: 1, lane: 'bot' },
        { id: 'r_t2_bot', x: 330, y: 450, team: 'radiant', type: 'tower', tier: 2, lane: 'bot' },
        { id: 'r_t3_bot', x: 150, y: 510, team: 'radiant', type: 'tower', tier: 3, lane: 'bot' },
        
        // Middle lane
        { id: 'r_t1_mid', x: 255, y: 350, team: 'radiant', type: 'tower', tier: 1, lane: 'mid' },
        { id: 'r_t2_mid', x: 200, y: 390, team: 'radiant', type: 'tower', tier: 2, lane: 'mid' },
        { id: 'r_t3_mid', x: 150, y: 450, team: 'radiant', type: 'tower', tier: 3, lane: 'mid' },
        
        // Top lane
        { id: 'r_t1_top', x: 150, y: 210, team: 'radiant', type: 'tower', tier: 1, lane: 'top' },
        { id: 'r_t2_top', x: 120, y: 300, team: 'radiant', type: 'tower', tier: 2, lane: 'top' },
        { id: 'r_t3_top', x: 120, y: 450, team: 'radiant', type: 'tower', tier: 3, lane: 'top' },
        
        // Radiant Base (Bottom Left corner in the image)
        { id: 'r_t4_1', x: 120, y: 480, team: 'radiant', type: 'tower', tier: 4 },
        { id: 'r_t4_2', x: 150, y: 480, team: 'radiant', type: 'tower', tier: 4 },
        { id: 'r_ancient', x: 135, y: 510, team: 'radiant', type: 'ancient' },
        
        // Radiant barracks
        { id: 'r_rax_top_melee', x: 120, y: 465, team: 'radiant', type: 'barracks', lane: 'top' },
        { id: 'r_rax_top_ranged', x: 135, y: 465, team: 'radiant', type: 'barracks', lane: 'top' },
        { id: 'r_rax_mid_melee', x: 150, y: 480, team: 'radiant', type: 'barracks', lane: 'mid' },
        { id: 'r_rax_mid_ranged', x: 165, y: 480, team: 'radiant', type: 'barracks', lane: 'mid' },
        { id: 'r_rax_bot_melee', x: 165, y: 510, team: 'radiant', type: 'barracks', lane: 'bot' },
        { id: 'r_rax_bot_ranged', x: 180, y: 510, team: 'radiant', type: 'barracks', lane: 'bot' },
        
        // Dire Structures (Red)
        // Top lane
        { id: 'd_t1_top', x: 135, y: 110, team: 'dire', type: 'tower', tier: 1, lane: 'top' },
        { id: 'd_t2_top', x: 285, y: 105, team: 'dire', type: 'tower', tier: 2, lane: 'top' },
        { id: 'd_t3_top', x: 420, y: 90, team: 'dire', type: 'tower', tier: 3, lane: 'top' },
        
        // Middle lane
        { id: 'd_t1_mid', x: 320, y: 280, team: 'dire', type: 'tower', tier: 1, lane: 'mid' },
        { id: 'd_t2_mid', x: 375, y: 235, team: 'dire', type: 'tower', tier: 2, lane: 'mid' },
        { id: 'd_t3_mid', x: 450, y: 120, team: 'dire', type: 'tower', tier: 3, lane: 'mid' },
        
        // Bottom lane
        { id: 'd_t1_bot', x: 450, y: 330, team: 'dire', type: 'tower', tier: 1, lane: 'bot' },
        { id: 'd_t2_bot', x: 510, y: 240, team: 'dire', type: 'tower', tier: 2, lane: 'bot' },
        { id: 'd_t3_bot', x: 510, y: 150, team: 'dire', type: 'tower', tier: 3, lane: 'bot' },
        
        // Dire Base (Top Right corner in the image)
        { id: 'd_t4_1', x: 465, y: 120, team: 'dire', type: 'tower', tier: 4 },
        { id: 'd_t4_2', x: 480, y: 105, team: 'dire', type: 'tower', tier: 4 },
        { id: 'd_ancient', x: 480, y: 90, team: 'dire', type: 'ancient' },
        
        // Dire barracks
        { id: 'd_rax_top_melee', x: 435, y: 90, team: 'dire', type: 'barracks', lane: 'top' },
        { id: 'd_rax_top_ranged', x: 450, y: 90, team: 'dire', type: 'barracks', lane: 'top' },
        { id: 'd_rax_mid_melee', x: 465, y: 105, team: 'dire', type: 'barracks', lane: 'mid' },
        { id: 'd_rax_mid_ranged', x: 450, y: 120, team: 'dire', type: 'barracks', lane: 'mid' },
        { id: 'd_rax_bot_melee', x: 510, y: 135, team: 'dire', type: 'barracks', lane: 'bot' },
        { id: 'd_rax_bot_ranged', x: 510, y: 120, team: 'dire', type: 'barracks', lane: 'bot' },
        
        // Outposts (yellow squares in the image)
        { id: 'radiant_outpost', x: 90, y: 330, team: 'radiant', type: 'shrine', lane: 'outpost' },
        { id: 'dire_outpost', x: 510, y: 270, team: 'dire', type: 'shrine', lane: 'outpost' },
        
        // Roshan (The circle in the river)
        { id: 'roshan', x: 225, y: 165, team: 'neutral', type: 'shrine' },
        
        // Bounty Runes (Yellow squares)
        { id: 'top_bounty', x: 75, y: 75, team: 'neutral', type: 'shrine', lane: 'jungle' },
        { id: 'bot_bounty', x: 525, y: 525, team: 'neutral', type: 'shrine', lane: 'jungle' },
        { id: 'radiant_triangle_bounty', x: 330, y: 525, team: 'neutral', type: 'shrine', lane: 'jungle' },
        { id: 'dire_triangle_bounty', x: 180, y: 75, team: 'neutral', type: 'shrine', lane: 'jungle' },
        
        // White circles (possibly neutral camps)
        { id: 'neutral_camp_1', x: 150, y: 120, team: 'neutral', type: 'shrine', lane: 'jungle' },
        { id: 'neutral_camp_2', x: 315, y: 240, team: 'neutral', type: 'shrine', lane: 'jungle' },
        { id: 'neutral_camp_3', x: 315, y: 315, team: 'neutral', type: 'shrine', lane: 'jungle' },
        { id: 'neutral_camp_4', x: 480, y: 360, team: 'neutral', type: 'shrine', lane: 'jungle' },
        { id: 'neutral_camp_5', x: 390, y: 510, team: 'neutral', type: 'shrine', lane: 'jungle' },
        { id: 'neutral_camp_6', x: 270, y: 450, team: 'neutral', type: 'shrine', lane: 'jungle' }
      ];
      
      setTowers(towerData);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching tower data:', error);
      setIsLoading(false);
    }
  }, []);

  // Tower marker component with critical fix for tower visibility
  const TowerMarker = ({ tower }: { tower: MapTower }) => {
    // CRITICAL FIX: Check if tower should be hidden based on current time point
    // This is the most important part - hiding towers at the right time
    if (tower.isDestroyed && tower.destroyedAt !== undefined) {
      // Only show towers if the current time is before their destruction time
      if (currentTimePoint >= tower.destroyedAt) {
        // Tower is destroyed at current time point - don't render it
        return null;
      }
    }
    
    const markerStyle: React.CSSProperties = {
      position: 'absolute',
      left: `${tower.x}px`,
      top: `${tower.y}px`,
      transform: 'translate(-50%, -50%)',
      width: getMarkerSize(tower),
      height: getMarkerSize(tower),
      backgroundColor: getMarkerColor(tower),
      borderRadius: getMarkerShape(tower),
      border: `2px solid ${getMarkerBorderColor(tower)}`,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      fontSize: '10px',
      color: '#fff',
      fontWeight: 'bold',
      zIndex: 20,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: '0 0 4px rgba(0, 0, 0, 0.5)'
    };

    const handleClick = () => {
      console.log(`Clicked on ${tower.team} ${tower.type} ${tower.tier || ''} at (${tower.x}, ${tower.y})`);
      if (tower.isDestroyed) {
        console.log(`This tower will be destroyed at ${Math.floor(tower.destroyedAt! / 60)}:${String(tower.destroyedAt! % 60).padStart(2, '0')}`);
      }
      setCoordinates({ x: tower.x, y: tower.y });
    };

    return (
      <div style={markerStyle} onClick={handleClick} title={`${tower.team} ${tower.type} ${tower.tier || ''}`}>
        {getTowerLabel(tower)}
      </div>
    );
  };

  // Make sure the useEffect forces a re-render every time currentTimePoint changes
  useEffect(() => {
    // This is CRITICAL to make the towers disappear at the right time
    console.log(`Time point changed to ${currentTimePoint}`);
    
    // We need to force a re-render by creating a new array with the same contents
    // This makes React re-evaluate the TowerMarker component for each tower
    setTowers(prevTowers => [...prevTowers]);
  }, [currentTimePoint]); // Only re-run when currentTimePoint changes

  // Improved fetch function to correctly process OpenDota API data
  const fetchTowerDestructionData = useCallback(async () => {
    if (!matchId) return;
    
    try {
      setIsLoading(true);
      console.log(`Fetching match data for match ID: ${matchId}`);
      
      // Fetch match data from OpenDota API
      const response = await fetch(`https://api.opendota.com/api/matches/${matchId}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch match data: ${response.status}`);
      }
      
      const matchData = await response.json();
      console.log("Full match data:", matchData);
      
      // Update game duration from match data
      if (matchData.duration && matchData.duration > 0) {
        setGameDuration(matchData.duration);
        console.log(`Setting game duration to ${matchData.duration} seconds`);
      }
      
      // Get the objectives data, which contains building destructions
      const objectives = matchData.objectives || [];
      console.log("All objectives from match:", objectives);
      
      // In OpenDota API, team 2 = radiant, team 3 = dire for ownership
      // But when a building is killed, the team number represents the team that did the kill
      const buildingKills = objectives.filter((obj: any) => obj.type === "BUILDING_KILL");
      console.log("Building kills:", buildingKills);
      
      // Map to keep track of which buildings have been processed
      const processedBuildings = new Set();
      
      // Update towers with destruction data
      setTowers(prevTowers => {
        const updatedTowers = prevTowers.map(tower => {
          // For each tower in our map data, try to find a matching destruction event
          for (const kill of buildingKills) {
            const buildingKey = kill.key?.toLowerCase() || '';
            // Skip already processed building kills to prevent duplicates
            if (processedBuildings.has(buildingKey)) continue;
            
            // Team that owns the building (2 = radiant, 3 = dire)
            const buildingTeam = kill.team === 2 ? 'dire' : 'radiant'; // The team attribute is who killed it
            
            // Skip if tower team doesn't match
            if (tower.team !== buildingTeam) continue;
            
            let isMatch = false;
            
            // Match tower
            if (tower.type === 'tower' && buildingKey.includes('tower')) {
              const tierMatch = buildingKey.match(/t(\d)/);
              const tier = tierMatch ? parseInt(tierMatch[1]) : null;
              
              // Match lane
              const isTopLane = buildingKey.includes('top');
              const isMidLane = buildingKey.includes('mid');
              const isBotLane = buildingKey.includes('bot');
              
              // Check for lane match
              const laneMatches = 
                (isTopLane && tower.lane === 'top') ||
                (isMidLane && tower.lane === 'mid') ||
                (isBotLane && tower.lane === 'bot') ||
                (!tower.lane && !isTopLane && !isMidLane && !isBotLane);
              
              // Match tier and lane
              isMatch = tower.tier === tier && laneMatches;
            }
            // Match barracks
            else if (tower.type === 'barracks' && buildingKey.includes('rax')) {
              const isTopLane = buildingKey.includes('top');
              const isMidLane = buildingKey.includes('mid');
              const isBotLane = buildingKey.includes('bot');
              
              const isRanged = buildingKey.includes('range');
              const isMelee = buildingKey.includes('melee');
              
              // Check lane match
              const laneMatches = 
                (isTopLane && tower.lane === 'top') ||
                (isMidLane && tower.lane === 'mid') ||
                (isBotLane && tower.lane === 'bot');
                
              // Check barracks type match  
              const typeMatches =
                (isRanged && tower.id.includes('ranged')) ||
                (isMelee && tower.id.includes('melee'));
                
              isMatch = laneMatches && typeMatches;
            }
            // Match ancient
            else if (tower.type === 'ancient' && (buildingKey.includes('ancient') || buildingKey.includes('fort'))) {
              isMatch = true;
            }
            
            // If we found a match, mark this building as destroyed and set its destruction time
            if (isMatch) {
              console.log(`Tower ${tower.id} matches destruction event:`, kill);
              processedBuildings.add(buildingKey);
              
              return {
                ...tower,
                isDestroyed: true,
                destroyedAt: kill.time
              };
            }
          }
          
          // No match found, tower remains intact
          return tower;
        });
        
        // Log all destroyed towers with their destruction times
        const destroyedTowers = updatedTowers.filter(t => t.isDestroyed);
        destroyedTowers.forEach(tower => {
          const minutes = Math.floor(tower.destroyedAt! / 60);
          const seconds = String(tower.destroyedAt! % 60).padStart(2, '0');
          console.log(`Tower ${tower.id} will be destroyed at ${minutes}:${seconds}`);
        });
        
        return updatedTowers;
      });
      
      // Set objectives for timeline visualization
      const timelineEvents = objectives
        .filter((obj: any) => obj.type === "BUILDING_KILL" || obj.type === "ROSHAN_KILL")
        .map((obj: any) => ({
          type: obj.type,
          time: obj.time,
          team: obj.team === 2 ? 'dire' : 'radiant', // Team that gets advantage
          key: obj.key
        }));
      
      console.log("Timeline events:", timelineEvents);
      setGameObjectives(timelineEvents);
      
      // Set time to beginning of game
      setCurrentTimePoint(0);
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching tower destruction data:', error);
      setIsLoading(false);
    }
  }, [matchId, setGameDuration, setGameObjectives, setCurrentTimePoint]);
  
  // Add a useEffect to automatically fetch data when matchId changes
  useEffect(() => {
    if (matchId) {
      fetchTowerData(); // First load tower positions
      fetchTowerDestructionData(); // Then load destruction data
    }
  }, [matchId, fetchTowerData, fetchTowerDestructionData]);
  
  // Helper functions for TowerMarker
  const getMarkerSize = (tower: MapTower): string => {
    if (tower.type === 'ancient') return '26px';
    if (tower.id === 'roshan') return '24px';
    if (tower.type === 'barracks') return '16px';
    if (tower.id.includes('neutral_camp')) return '10px';
    if (tower.type === 'shrine') return '18px';
    return '20px';
  };

  const getMarkerColor = (tower: MapTower): string => {
    if (tower.team === 'radiant') return '#3CFF00';
    if (tower.team === 'dire') return '#FF0000';
    if (tower.id.includes('neutral_camp')) return '#FFFFFF';
    if (tower.id.includes('bounty')) return '#FFD700';
    if (tower.id === 'roshan') return '#FFA500';
    return '#808080';
  };

  const getMarkerBorderColor = (tower: MapTower): string => {
    return '#000000';
  };

  const getMarkerShape = (tower: MapTower): string => {
    return '0px'; // Square markers for all types
  };

  const getTowerLabel = (tower: MapTower): string => {
    if (tower.type === 'ancient') return 'A';
    if (tower.type === 'barracks') return tower.id.includes('melee') ? 'M' : 'R';
    if (tower.type === 'tower') return tower.tier?.toString() || '';
    if (tower.lane === 'outpost') return 'O';
    return '';
  };

  // Update towers directly when the timeline changes to force re-render
  useEffect(() => {
    // This is critical - force re-render all towers when timeline changes
    console.log(`Timeline changed to ${currentTimePoint}, updating tower visibility`);
    // By creating a new array, we force React to re-render all towers
    setTowers(prevTowers => [...prevTowers]);
  }, [currentTimePoint]);

  // Function to convert Dota 2 map coordinates to our map pixel coordinates
  const convertDotaToPixelCoords = (x: number, y: number): { x: number; y: number } => {
    // Dota 2 map coordinates range:
    // x: -8192 to 8192, where -8192 is the bottom left of the map
    // y: -8192 to 8192, where 8192 is the top right of the map
    
    // Our map size is 600x600 pixels
    const mapSize = 600;
    
    // Convert from Dota coords to our pixel coords
    // Scale and offset to fit our map image
    const pixelX = Math.round(((x + 8192) / 16384) * mapSize);
    const pixelY = Math.round(mapSize - ((y + 8192) / 16384) * mapSize);
    
    return { x: pixelX, y: pixelY };
  };

  // Function to fetch hero positions over time from OpenDota API
  const fetchHeroPositions = useCallback(async () => {
    if (!matchId) return;
    
    try {
      setIsLoading(true);
      console.log(`Fetching hero positions for match ID: ${matchId}`);
      
      // Fetch player data from OpenDota API
      const response = await fetch(`https://api.opendota.com/api/matches/${matchId}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch match data: ${response.status}`);
      }
      
      const matchData = await response.json();
      
      // Get player data and positions
      const playerData = matchData.players || [];
      console.log("Player data:", playerData);
      
      // Initialize hero position data
      const heroes: HeroPosition[] = [];
      
      // Process each player's movements from their positions
      for (const player of playerData) {
        if (!player.hero_id) continue;
        
        // Get positions data (if available)
        const positionsData = player.positions || [];
        console.log(`Player ${player.hero_id} positions:`, positionsData);
        
        // Team mapping: 0 = radiant, 1 = dire
        const team = player.isRadiant ? 'radiant' : 'dire';
        
        // Create a color based on player slot for distinction
        const colors = [
          '#3498db', '#2ecc71', '#9b59b6', '#f1c40f', '#e74c3c',
          '#1abc9c', '#e67e22', '#34495e', '#95a5a6', '#d35400'
        ];
        const color = colors[player.player_slot % colors.length];
        
        // Process positions
        const positions = positionsData.map((pos: any) => {
          // Convert dota coordinates to our pixel coordinates
          const pixelCoords = convertDotaToPixelCoords(pos.x, pos.y);
          return {
            time: pos.time,
            x: pixelCoords.x,
            y: pixelCoords.y
          };
        });
        
        // Make sure we have sorted positions by time
        positions.sort((a: {time: number}, b: {time: number}) => a.time - b.time);
        
        // Get hero image URL
        const heroId = player.hero_id;
        const heroName = getHeroNameById(heroId) || 'unknown';
        const heroImageUrl = `https://cdn.cloudflare.steamstatic.com/apps/dota2/images/heroes/${heroName}_full.png`;
        
        heroes.push({
          id: player.player_slot,
          heroId,
          name: heroName,
          team,
          color,
          imageUrl: heroImageUrl,
          positions
        });
      }
      
      console.log("Processed hero position data:", heroes);
      setHeroPositions(heroes);
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching hero position data:', error);
      setIsLoading(false);
    }
  }, [matchId]);
  
  // Helper function to get hero name from hero ID
  const getHeroNameById = (heroId: number): string => {
    // This is a very basic mapping for common heroes
    // In a real app, you'd use a complete hero mapping
    const heroMap: Record<number, string> = {
      1: 'antimage',
      2: 'axe',
      3: 'bane',
      4: 'bloodseeker',
      5: 'crystal_maiden',
      6: 'drow_ranger',
      7: 'earthshaker',
      8: 'juggernaut',
      9: 'mirana',
      10: 'morphling',
      11: 'nevermore',
      // Add more heroes as needed
    };
    
    return heroMap[heroId] || `hero_${heroId}`;
  };

  // Update useEffect to also fetch hero positions
  useEffect(() => {
    if (matchId) {
      fetchTowerData();
      fetchTowerDestructionData();
      fetchHeroPositions();
    }
  }, [matchId, fetchTowerData, fetchTowerDestructionData, fetchHeroPositions]);

  // Hero marker component to show hero positions on the map
  const HeroMarker = ({ hero }: { hero: HeroPosition }) => {
    // Find the position closest to the current timeline point
    const currentPosition = useMemo(() => {
      // If no positions data, return null
      if (!hero.positions || hero.positions.length === 0) return null;
      
      // If current time is before first position, use the first position
      if (currentTimePoint < hero.positions[0].time) {
        return hero.positions[0];
      }
      
      // If current time is after last position, use the last position
      if (currentTimePoint > hero.positions[hero.positions.length - 1].time) {
        return hero.positions[hero.positions.length - 1];
      }
      
      // Find the position just before the current time
      for (let i = 0; i < hero.positions.length - 1; i++) {
        if (hero.positions[i].time <= currentTimePoint && hero.positions[i + 1].time > currentTimePoint) {
          // Optionally, we could interpolate between positions for smoother movement
          return hero.positions[i];
        }
      }
      
      // Fallback to last position
      return hero.positions[hero.positions.length - 1];
    }, [hero.positions, currentTimePoint]);
    
    // If no position found, don't render
    if (!currentPosition) return null;
    
    const markerStyle: React.CSSProperties = {
      position: 'absolute',
      left: `${currentPosition.x}px`,
      top: `${currentPosition.y}px`,
      transform: 'translate(-50%, -50%)',
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      border: `2px solid ${hero.color}`,
      backgroundColor: `${hero.team === 'radiant' ? 'rgba(0, 255, 0, 0.3)' : 'rgba(255, 0, 0, 0.3)'}`,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 25,
      cursor: 'pointer',
      backgroundImage: `url(${hero.imageUrl})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      boxShadow: '0 0 5px rgba(0, 0, 0, 0.7)'
    };
    
    return (
      <div 
        style={markerStyle} 
        title={`${hero.name} (${hero.team})`}
        onClick={() => console.log(`Hero ${hero.name} at (${currentPosition.x}, ${currentPosition.y}), time: ${currentPosition.time}`)}
      />
    );
  };

  return (
    <div className="scenes-container">
      <div className="match-overview">
        <div className="team-tabs" style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '20px'
        }}>
          <div>
            <button 
              className={`team-tab ${selectedTeam === 'radiant' ? 'active' : ''}`}
              style={{
                padding: '10px 20px',
                background: selectedTeam === 'radiant' ? '#333' : '#222',
                border: 'none',
                color: 'white',
                marginRight: '5px',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
              onClick={() => setSelectedTeam('radiant')}
            >
              Team Spirit
            </button>
            <button 
              className={`team-tab ${selectedTeam === 'dire' ? 'active' : ''}`}
              style={{
                padding: '10px 20px',
                background: selectedTeam === 'dire' ? '#333' : '#222',
                border: 'none',
                color: 'white',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
              onClick={() => setSelectedTeam('dire')}
            >
              PARIVISION
            </button>
          </div>
          <button 
            className="share-session-btn"
            style={{
              padding: '10px 15px',
              background: '#4CAF50',
              border: 'none',
              color: 'white',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Share Session
          </button>
        </div>
        
        {/* Team Net Worth Display - Matching ItemizationSimulation */}
        <div style={{
          display: 'flex',
          gap: '20px',
          margin: '15px 0',
          padding: '15px',
          background: '#1a1a1a',
          borderRadius: '5px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
          position: 'sticky',
          top: '50px',
          zIndex: 90
        }}>
          {/* Radiant Team */}
          <div style={{ flex: 1 }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              marginBottom: '10px',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, color: '#66bb6a', display: 'flex', alignItems: 'center' }}>
                Team Spirit
                <span style={{ marginLeft: '10px', fontSize: '16px', fontWeight: 'normal' }}>
                  <span style={{ color: '#ffeb3b', fontWeight: 'bold' }}>{radiantTotalNetWorth.toLocaleString()}</span> <span style={{ color: '#ffeb3b', fontSize: '14px' }}>gold</span>
                </span>
              </h3>
            </div>
            
            {radiantData.map(({ hero, netWorth, kda }) => {
              const maxNetWorth = Math.max(...radiantData.map(h => h.netWorth), 1);
              const widthPercent = (netWorth / maxNetWorth) * 100;
              
              return (
                <div key={hero.id} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  marginBottom: '5px',
                  color: selectedTeam === 'radiant' ? 'white' : '#aaa',
                  height: '38px'
                }}>
                  {/* Hero icon and items */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    width: '310px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      marginRight: '8px',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      background: '#1e1e1e',
                      border: '1px solid #333',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flexShrink: 0
                    }}>
                      <img 
                        src={hero.imageUrl} 
                        alt={hero.name} 
                        style={{ 
                          width: '44px', 
                          height: '44px',
                          objectFit: 'cover',
                          opacity: selectedTeam === 'radiant' ? 1 : 0.7
                        }}
                        title={hero.name}
                      />
                    </div>
                    {/* Add KDA display */}
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      marginRight: '8px',
                      minWidth: '60px'
                    }}>
                      <div style={{ fontSize: '12px', color: '#ccc' }}>{hero.name}</div>
                      <div style={{ 
                        fontSize: '11px', 
                        padding: '2px 4px', 
                        background: 'rgba(0,0,0,0.3)', 
                        borderRadius: '3px',
                        display: 'inline-block'
                      }}>
                        {kda}
                      </div>
                    </div>
                    {/* Display hero's current items */}
                    <div style={{ 
                      display: 'flex',
                      gap: '4px',
                      height: '36px',
                      alignItems: 'center',
                      flexGrow: 1,
                      overflow: 'hidden'
                    }}>
                      <span style={{ 
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        color: '#777',
                        fontStyle: 'italic'
                      }}>No items</span>
                    </div>
                  </div>
                  
                  {/* Net worth bar */}
                  <div style={{ flex: 0.7, position: 'relative', height: '20px', marginLeft: '5px' }}>
                    <div style={{ 
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      height: '100%',
                      width: `${widthPercent}%`,
                      background: 'rgba(102, 187, 106, 0.3)',
                      borderRadius: '2px',
                      transition: 'width 0.3s'
                    }}></div>
                    
                    <div style={{ 
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      width: '100%',
                      padding: '0 5px',
                    }}>
                      <span style={{ fontSize: '11px', color: '#ffeb3b' }}>
                        {netWorth.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Advantage indicator in the middle */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 15px'
          }}>
            <div style={{ fontSize: '12px', color: '#aaa', marginBottom: '5px' }}>Advantage</div>
            <div style={{ 
              fontSize: '14px', 
              fontWeight: 'bold',
              color: advantage.colorClass
            }}>
              {advantage.displayText}
            </div>
            {Math.abs(direTotalNetWorth - radiantTotalNetWorth) > 5000 && (
              <>
                <div style={{ 
                  fontSize: '12px', 
                  color: advantage.colorClass,
                  marginTop: '5px' 
                }}>
                  {advantage.team} {Math.floor(advantage.value / 1000)}k+
                </div>
                <div style={{ 
                  fontSize: '12px', 
                  color: '#ffeb3b' 
                }}>
                  {advantage.value.toLocaleString()} gold
                </div>
              </>
            )}
          </div>
          
          {/* Dire Team */}
          <div style={{ flex: 1 }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              marginBottom: '10px',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, color: '#ef5350', display: 'flex', alignItems: 'center' }}>
                PARIVISION
                <span style={{ marginLeft: '10px', fontSize: '16px', fontWeight: 'normal' }}>
                  <span style={{ color: '#ffeb3b', fontWeight: 'bold' }}>{direTotalNetWorth.toLocaleString()}</span> <span style={{ color: '#ffeb3b', fontSize: '14px' }}>gold</span>
                </span>
              </h3>
            </div>
            
            {direData.map(({ hero, netWorth, kda }) => {
              const maxNetWorth = Math.max(...direData.map(h => h.netWorth), 1);
              const widthPercent = (netWorth / maxNetWorth) * 100;
              
              return (
                <div key={hero.id} style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  marginBottom: '5px',
                  color: selectedTeam === 'dire' ? 'white' : '#aaa',
                  height: '38px'
                }}>
                  {/* Hero icon and items */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    width: '310px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      marginRight: '8px',
                      borderRadius: '4px',
                      overflow: 'hidden',
                      background: '#1e1e1e',
                      border: '1px solid #333',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flexShrink: 0
                    }}>
                      <img 
                        src={hero.imageUrl} 
                        alt={hero.name} 
                        style={{ 
                          width: '44px', 
                          height: '44px',
                          objectFit: 'cover',
                          opacity: selectedTeam === 'dire' ? 1 : 0.7
                        }}
                        title={hero.name}
                      />
                    </div>
                    {/* Add KDA display */}
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      marginRight: '8px',
                      minWidth: '60px'
                    }}>
                      <div style={{ fontSize: '12px', color: '#ccc' }}>{hero.name}</div>
                      <div style={{ 
                        fontSize: '11px', 
                        padding: '2px 4px', 
                        background: 'rgba(0,0,0,0.3)', 
                        borderRadius: '3px',
                        display: 'inline-block'
                      }}>
                        {kda}
                      </div>
                    </div>
                    {/* Display hero's current items */}
                    <div style={{ 
                      display: 'flex',
                      gap: '4px',
                      height: '36px',
                      alignItems: 'center',
                      flexGrow: 1,
                      overflow: 'hidden'
                    }}>
                      <span style={{ 
                        fontSize: '12px',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        color: '#777',
                        fontStyle: 'italic'
                      }}>No items</span>
                    </div>
                  </div>
                  
                  {/* Net worth bar */}
                  <div style={{ flex: 0.7, position: 'relative', height: '20px', marginLeft: '5px' }}>
                    <div style={{ 
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      height: '100%',
                      width: `${widthPercent}%`,
                      background: 'rgba(239, 83, 80, 0.3)',
                      borderRadius: '2px',
                      transition: 'width 0.3s'
                    }}></div>
                    
                    <div style={{ 
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      width: '100%',
                      padding: '0 5px',
                    }}>
                      <span style={{ fontSize: '11px', color: '#ffeb3b' }}>
                        {netWorth.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Timeline Slider Controls - Match the exact UI from ItemizationSimulation */}
        <div className="timeline-controls" style={{
          margin: '20px 0',
          padding: '15px',
          background: '#1a1a1a',
          borderRadius: '5px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
          position: 'sticky',
          top: '300px',
          zIndex: 80
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
            <h3 style={{ margin: 0, fontSize: '18px' }}>Game Timeline</h3>
            <div>
              <button 
                onClick={() => setIsPlaying(!isPlaying)}
                style={{
                  background: isPlaying ? '#f44336' : '#4caf50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '8px 16px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  fontWeight: 'bold'
                }}
              >
                {isPlaying ? (
                  <>
                    <span style={{ fontSize: '18px' }}>⏸</span> Pause
                  </>
                ) : (
                  <>
                    <span style={{ fontSize: '18px' }}>▶️</span> Play
                  </>
                )}
              </button>
            </div>
          </div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            margin: '15px 0',
            width: '100%'
          }}>
            <span style={{ minWidth: '45px', textAlign: 'center', fontWeight: 'bold' }}>
              {Math.floor(currentTimePoint / 60)}:{String(currentTimePoint % 60).padStart(2, '0')}
            </span>
            <input
              type="range"
              min="0"
              max={gameDuration}
              value={currentTimePoint}
              onChange={(e) => {
                setCurrentTimePoint(parseInt(e.target.value));
                // Pause playback when user manually changes the slider
                if (isPlaying) setIsPlaying(false);
              }}
              style={{
                flexGrow: 1,
                height: '12px',
                borderRadius: '6px',
                width: '100%'
              }}
            />
            <span style={{ minWidth: '45px', textAlign: 'center' }}>
              {Math.floor(gameDuration / 60)}:{String(gameDuration % 60).padStart(2, '0')}
            </span>
          </div>
          
          {/* Game Objectives Timeline */}
          <div className="objectives-timeline" style={{
            marginTop: '15px',
            position: 'relative',
            height: '36px',
            background: '#2a2a2a',
            borderRadius: '4px',
            overflow: 'hidden',
            border: '1px solid #333',
            width: '100%'
          }}>
            {gameObjectives.map((obj, idx) => {
              const positionPercent = (obj.time / gameDuration) * 100;
              const color = obj.team === 'radiant' ? '#66bb6a' : '#ef5350';
              const icon = obj.type === 'ROSHAN_KILL' ? '🐉' : '🏰';
              
              return (
                <div
                  key={idx}
                  style={{
                    position: 'absolute',
                    left: `${positionPercent}%`,
                    top: '0',
                    transform: 'translateX(-50%)',
                    width: '20px',
                    height: '20px',
                    background: color,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    cursor: 'pointer',
                    zIndex: 2
                  }}
                  title={`${obj.type} at ${Math.floor(obj.time / 60)}:${String(obj.time % 60).padStart(2, '0')} by ${obj.team}`}
                >
                  {icon}
                </div>
              );
            })}
            {/* Current time marker */}
            <div style={{
              position: 'absolute',
              left: `${(currentTimePoint / gameDuration) * 100}%`,
              top: '0',
              height: '100%',
              width: '2px',
              background: 'white',
              zIndex: 1
            }}></div>
          </div>
        </div>

        <div className="dota-map-container">
          <h3>Map Overview</h3>
          <div style={{ 
            position: 'relative', 
            width: '600px', 
            height: '600px',
            margin: '0 auto',
            border: '1px solid #333',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            overflow: 'hidden',
            backgroundImage: 'url("https://media-hosting.imagekit.io/be4fb13fb9dd4416/600px-Gamemap_7.38_simplemap_dota2_gameasset.png?Expires=1839498760&Key-Pair-Id=K2ZIVPTIP2VGHC&Signature=oudlQUD2V~sBuvVgblBycnC4zP04kQ6h7P-CXfM69QCTb0xjl2-rko6Od9etmpyhmFivCRB37EoYrg0Y-5ISoFCGcINK4vugQyskb-ohH5thHo27AZFUnGQ5FnJFcscZ~I-~-Ekafz0iBpNiCrTO0OxSSCX0guTb~vIWl4z-BIjZdNO09AApwxUCk74VvqXuZxXqoGGsnJCMu8czvTj~HczDe8lihx5cztAFaNWm3lsj23uy759ReGAYjIWW3HyQovEqNRn6Db7aLNw7tvfdKPI5tzlEkMraNmuTSESoj-UF0~4dQNU8H9LCC5x29DdQ2ofil2Ps1Ctf4MDbQo-EAQ__")',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }} ref={mapRef} onClick={handleMapClick}>
            
            {/* Grid lines - 20x20 grid (every 30px) */}
            {showGrid && (
              <>
                {/* Vertical grid lines */}
                {Array.from({ length: 21 }).map((_, i) => (
                  <div key={`vertical-${i}`} style={{
                    position: 'absolute',
                    left: `${i * 30}px`,
                    top: 0,
                    width: '1px',
                    height: '100%',
                    background: i % 5 === 0 ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.2)',
                    zIndex: 2,
                    pointerEvents: 'none'
                  }} />
                ))}
                
                {/* Horizontal grid lines */}
                {Array.from({ length: 21 }).map((_, i) => (
                  <div key={`horizontal-${i}`} style={{
                    position: 'absolute',
                    top: `${i * 30}px`,
                    left: 0,
                    height: '1px',
                    width: '100%',
                    background: i % 5 === 0 ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.2)',
                    zIndex: 2,
                    pointerEvents: 'none'
                  }} />
                ))}
                
                {/* Grid coordinates */}
                {Array.from({ length: 6 }).map((_, i) => (
                  Array.from({ length: 6 }).map((_, j) => (
                    <div 
                      key={`coord-${i}-${j}`}
                      style={{
                        position: 'absolute',
                        top: `${i * 5 * 30 + 5}px`,
                        left: `${j * 5 * 30 + 5}px`,
                        background: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        fontSize: '10px',
                        padding: '2px 4px',
                        borderRadius: '3px',
                        zIndex: 3,
                        pointerEvents: 'none'
                      }}
                    >
                      {j*5},{i*5}
                    </div>
                  ))
                ))}
              </>
            )}
            
            {/* Tower markers - No need to filter here, we handle visibility in the TowerMarker component */}
            {towers.map(tower => (
              <TowerMarker key={tower.id} tower={tower} />
            ))}
            
            {/* Hero position markers */}
            {heroPositions.map(hero => (
              <HeroMarker key={hero.id} hero={hero} />
            ))}
            
            {/* Show coordinates when clicked */}
            {coordinates && (
              <div style={{
                position: 'absolute',
                bottom: '10px',
                left: '10px',
                background: 'rgba(0,0,0,0.8)',
                color: 'white',
                padding: '5px 10px',
                borderRadius: '4px',
                fontSize: '14px',
                zIndex: 10
              }}>
                Coordinates: x={coordinates.x * 30}, y={coordinates.y * 30}
              </div>
            )}
          </div>
          
          {/* Control buttons */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            marginTop: '10px',
            gap: '10px'
          }}>
            <button 
              style={{
                background: showGrid ? '#66bb6a' : '#2a2a2a',
                border: '1px solid #444',
                borderRadius: '4px',
                padding: '5px 10px',
                color: showGrid ? 'white' : '#ccc',
                fontSize: '12px',
                cursor: 'pointer'
              }}
              onClick={() => setShowGrid(prev => !prev)}
            >
              {showGrid ? 'Hide Grid' : 'Show Grid'}
            </button>
            
            <button 
              style={{
                background: towers.length > 0 ? '#66bb6a' : '#2a2a2a',
                border: '1px solid #444',
                borderRadius: '4px',
                padding: '5px 10px',
                color: towers.length > 0 ? 'white' : '#ccc',
                fontSize: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={fetchTowerData}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span style={{ 
                    display: 'inline-block', 
                    width: '12px', 
                    height: '12px', 
                    border: '2px solid #aaa',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    marginRight: '5px',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Loading...
                </>
              ) : (
                towers.length > 0 ? 'Refresh Tower Data' : 'Load Tower Data'
              )}
            </button>

            <button 
              style={{
                background: '#2a2a2a',
                border: '1px solid #444',
                borderRadius: '4px',
                padding: '5px 10px',
                color: '#ccc',
                fontSize: '12px',
                cursor: 'pointer'
              }}
              onClick={() => setTowers([])}
            >
              Clear Towers
            </button>

            <button 
              style={{
                background: '#4a6baf',
                border: '1px solid #444',
                borderRadius: '4px',
                padding: '5px 10px',
                color: 'white',
                fontSize: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={fetchTowerDestructionData}
              disabled={isLoading || !matchId}
            >
              {isLoading ? (
                <>
                  <span style={{ 
                    display: 'inline-block', 
                    width: '12px', 
                    height: '12px', 
                    border: '2px solid #aaa',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    marginRight: '5px',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Loading...
                </>
              ) : (
                'Load Tower Destruction Data'
              )}
            </button>

            <button 
              style={{
                background: '#8a2be2',
                border: '1px solid #444',
                borderRadius: '4px',
                padding: '5px 10px',
                color: 'white',
                fontSize: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={fetchHeroPositions}
              disabled={isLoading || !matchId}
            >
              {isLoading ? (
                <>
                  <span style={{ 
                    display: 'inline-block', 
                    width: '12px', 
                    height: '12px', 
                    border: '2px solid #aaa',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    marginRight: '5px',
                    animation: 'spin 1s linear infinite'
                  }} />
                  Loading...
                </>
              ) : (
                'Load Hero Movements'
              )}
            </button>

            <input
              type="text"
              value={matchId || ''}
              onChange={(e) => setMatchId(e.target.value)}
              placeholder="Enter Match ID"
              style={{
                padding: '5px 10px',
                borderRadius: '4px',
                border: '1px solid #444',
                background: '#2a2a2a',
                color: 'white',
                width: '150px'
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Scenes; 